/**
 * Секреты игроков и список своих партий.
 *
 * Учётных записей у игры нет: секрет, полученный при входе, и есть
 * удостоверение. Хранится по партиям, чтобы в одном браузере можно было
 * держать несколько, и вместе с именем и временем — иначе список своих
 * партий было бы не из чего собрать.
 *
 * Ходит в хранилище устройства через фасад `kv` (platform/kv): вебу за ним
 * localStorage, приложению — память поверх AsyncStorage, а этому модулю
 * разницы не видно.
 */
import { kv } from '@/shared/platform/kv'

const KEY = 'ludo.games'
const NAME_KEY = 'ludo.name'

export type SavedGame = {
  id: string
  secret: string
  name: string
  at: number
  /**
   * Партия доиграна.
   *
   * Секрет при этом не выбрасывается: по ссылке законченную партию можно
   * открыть и посмотреть итог. Флаг нужен только затем, чтобы её не
   * предлагали продолжить — продолжать там нечего.
   */
  over?: boolean
}

type Saved = Record<string, SavedGame>

function read(): Saved {
  try {
    const raw = kv.get(KEY)

    return raw === null ? {} : (JSON.parse(raw) as Saved)
  } catch {
    // Битая запись не должна ронять приложение: без секрета игрок просто
    // окажется зрителем и войдёт заново.
    return {}
  }
}

function write(saved: Saved): void {
  kv.set(KEY, JSON.stringify(saved))
}

/**
 * Имя игрока.
 *
 * Хранится отдельно от партий: человек один и тот же, а партий у него много,
 * и вводить имя заново на каждой — работа без смысла.
 *
 * Гостю имя придумывается само, и спрашивать его до игры больше не нужно.
 * Поле «Ваше имя» стояло перед кнопками и выключало их, пока оно пустое:
 * первым действием в игре был ввод текста с экранной клавиатуры, и на
 * телефоне на этом месте уходили. Придуманное имя сразу запоминается —
 * человек за столом должен узнавать себя от партии к партии, — а сменить
 * его можно на главной.
 */
export function savedName(): string {
  const saved = kv.get(NAME_KEY)

  if (saved !== null && saved.trim() !== '') return saved

  const made = madeUpName()

  // Приватный режим: kv молча не запишет — имя будет своё на каждую
  // загрузку, но играть можно.
  kv.set(NAME_KEY, made)

  return made
}

/**
 * Зверь и число: «Ёж 42».
 *
 * Не человеческое имя — такими подписаны компьютерные соперники
 * (config/ludo.php), и «Аня» за столом читалась бы как ещё один бот. И не
 * «Гость»: трое гостей за одним столом неразличимы.
 */
const NICKS = [
  'Лис',
  'Ёж',
  'Кот',
  'Енот',
  'Бобр',
  'Волк',
  'Барсук',
  'Филин',
  'Сокол',
  'Тигр',
  'Рысь',
  'Лемур',
  'Панда',
  'Кит',
  'Жираф',
  'Хорёк',
  'Ворон',
  'Дельфин',
]

function madeUpName(): string {
  const nick = NICKS[Math.floor(Math.random() * NICKS.length)]

  return `${nick} ${10 + Math.floor(Math.random() * 90)}`
}

export function rememberName(name: string): void {
  kv.set(NAME_KEY, name)
}

export function secretOf(gameId: string): string | null {
  return read()[gameId]?.secret ?? null
}

export function rememberSecret(gameId: string, secret: string, name = ''): void {
  const saved = read()
  const known = saved[gameId]

  /*
   * Известная запись ДОПОЛНЯЕТСЯ, а не пересобирается.
   *
   * Собиралась заново — и теряла отметку «доиграна»: стоило открыть
   * законченную партию по личной ссылке (/g/id?p=secret, см. App), как она
   * снова оказывалась в «Продолжить», и так каждый раз.
   */
  saved[gameId] = {
    ...known,
    id: gameId,
    secret,
    name: name || (known?.name ?? ''),
    at: Date.now(),
  }

  write(saved)
}

/**
 * Забыть все партии.
 *
 * Вызывается при выходе из учётной записи: секрет игрока — такое же
 * удостоверение, как и сессия, и оставлять его в браузере после выхода
 * значит оставить возможность ходить за того, кто уже вышел.
 */
export function forgetAllGames(): void {
  kv.remove(KEY)
}

/** Забыть партию: из неё вышли, и возвращаться в неё больше некуда. */
export function forgetGame(gameId: string): void {
  const saved = read()
  delete saved[gameId]
  write(saved)
}

/** Свои партии, свежие сверху. */
export function myGames(): SavedGame[] {
  return Object.values(read()).sort((a, b) => b.at - a.at)
}

/**
 * Последняя партия, которую есть смысл продолжать.
 *
 * Доигранные пропускаются: кнопка «Продолжить» на главной ведёт в игру, а не
 * в архив, и предлагать вернуться туда, где всё кончено, значит обещать не то.
 */
export function lastUnfinished(): SavedGame | undefined {
  return myGames().find((game) => game.over !== true)
}

/** Запомнить, что партия доиграна. */
export function markOver(gameId: string): void {
  const saved = read()
  const known = saved[gameId]

  if (known === undefined || known.over === true) return

  saved[gameId] = { ...known, over: true }

  write(saved)
}
